import { ModuleImportError } from "@/util/custom-errors.mjs";
import { devError } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import retry from "@/util/retry-promise.mjs";

/* eslint-disable no-useless-escape */

const pathHashMap = new Map(
  (globals.__BLITZ_ENTRY_POINTS__ ?? []).map((path) => {
    const hash = path.match(/[\/\\]([^\/]*)-([^\/]*)\./)?.[2];
    if (!hash) {
      devError(
        `Failed to map hash for path "${path}"! Might need to rebuild the index.html file.`,
      );

      return [path, hash];
    }
    return [path.replace(`-${hash}`, ""), hash];
  }),
);

export function resolveDynamicImportUrl(url: string) {
  const [path] = url.match(/\/js\/.*$/) ?? [];
  const hash = path && pathHashMap.get(path);

  const importUrl =
    !path || !hash
      ? url
      : url.replace(/\/([\w\.-]+)\.(\w+)$/, `/$1-${hash}.$2`);

  return importUrl;
}

/**
 * This function is necessary for appending the hash in dynamic entry points.
 * Note that this is only required when a dynamic import can't be hardcoded.
 */
export default function dynamicImport(url: string) {
  const importUrl = resolveDynamicImportUrl(url);

  try {
    return retry(() => import(importUrl));
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      const importError = new ModuleImportError(error.message);
      if ("stack" in error) importError.stack = error["stack"] as string;
      throw importError;
    } else {
      throw error;
    }
  }
}
